/**
 * URL-first preview helpers for Cart Companion Phase 0.
 * Always produce a valid preview from a parsed retailer URL — no scraping required.
 */

import type { ParsedRetailerUrl } from './retailerUrlParser';

function titleCaseSlug(slug: string): string {
  const small = new Set(['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'with']);
  return slug
    .split(/\s+/)
    .map((word, i) => {
      if (/^[A-Z0-9]{1,5}$/.test(word)) return word;
      if (i > 0 && small.has(word.toLowerCase())) return word.toLowerCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/** Best-effort product name from URL path (mirrors server slug extraction). */
export function titleFromUrlPath(rawUrl: string): string | null {
  try {
    const path = decodeURIComponent(new URL(rawUrl.trim()).pathname);

    const amazon = path.match(/^\/([^/]+)\/dp\//i);
    if (amazon && amazon[1].length > 3) {
      return titleCaseSlug(amazon[1].replace(/-/g, ' '));
    }

    const bestbuy = path.match(/^\/product\/([^/]+)\//i);
    if (bestbuy && bestbuy[1].length > 3) {
      return titleCaseSlug(bestbuy[1].replace(/-/g, ' '));
    }

    const walmart = path.match(/^\/ip\/([^/]+)\/\d+/i);
    if (walmart && walmart[1].length > 3) {
      return titleCaseSlug(walmart[1].replace(/-/g, ' '));
    }

    const target = path.match(/^\/p\/([^/]+)\//i);
    if (target && target[1].length > 3) {
      return titleCaseSlug(target[1].replace(/-/g, ' '));
    }

    const segments = path.split('/').filter((s) => s && !/^\d+$/.test(s));
    if (segments.length > 0) {
      const best = segments.reduce((a, b) => (b.length > a.length ? b : a));
      const title = best.replace(/[-_]/g, ' ').trim();
      if (title.length > 3) return titleCaseSlug(title);
    }
  } catch {
    /* invalid URL */
  }
  return null;
}

export function buildUrlFirstTitle(parsed: ParsedRetailerUrl): string {
  return titleFromUrlPath(parsed.originalUrl) ?? `${parsed.retailerLabel} item`;
}

/** True when the title is URL-first filler (safe to overwrite on enrichment). */
export function isPlaceholderTitle(
  title: string | undefined | null,
  url: string,
  retailer?: string,
): boolean {
  const t = (title ?? '').trim();
  if (!t) return true;
  if (t.toLowerCase().endsWith(' item')) return true;
  if (retailer && t.toLowerCase() === retailer.toLowerCase()) return true;
  const slugTitle = titleFromUrlPath(url);
  if (slugTitle && t.toLowerCase() === slugTitle.trim().toLowerCase()) return true;
  if (t.length <= 20 && !t.includes(' ') && /^[A-Za-z0-9]+$/.test(t)) return true;
  return false;
}

export function truncateUrlForDisplay(url: string, maxLen = 48): string {
  if (url.length <= maxLen) return url;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    const path = u.pathname.length > 24 ? `${u.pathname.slice(0, 22)}…` : u.pathname;
    const display = `${host}${path}`;
    return display.length <= maxLen ? display : `${display.slice(0, maxLen - 1)}…`;
  } catch {
    return `${url.slice(0, maxLen - 1)}…`;
  }
}
